import { createClient } from '@supabase/supabase-js';
import { getConfig, envConsole, isDevelopment } from '@/config/environments';

// Utility function to validate URL
function isValidUrl(url: string): boolean {
  if (!url || url.trim() === '') return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Get environment-specific configuration
const config = getConfig();

// Validate URLs before creating client
const hasValidUrl = isValidUrl(config.supabase.url);
const hasValidAnonKey = !!(config.supabase.anonKey && config.supabase.anonKey.trim() !== '');

if (!hasValidUrl || !hasValidAnonKey) {
  console.error('Invalid Supabase configuration:', {
    urlValid: hasValidUrl,
    anonKeyValid: hasValidAnonKey,
    url: config.supabase.url ? `${config.supabase.url.substring(0, 30)}...` : 'none'
  });
  
  if (isDevelopment()) {
    throw new Error('Invalid Supabase configuration - check your environment variables');
  }
}

envConsole.log('Initializing Supabase client for environment:', config.name);
envConsole.debug('Using Supabase URL:', config.supabase.url);

// Create Supabase client with environment-specific configuration only if valid
let supabase: ReturnType<typeof createClient>;

try {
  if (hasValidUrl && hasValidAnonKey) {
    supabase = createClient(
      config.supabase.url,
      config.supabase.anonKey,
      {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          detectSessionInUrl: true,
        },
        // Add environment-specific options
        global: {
          headers: {
            'X-Client-Info': `lokalaproducenter-${config.name}`,
          },
        },
      }
    );
    
    // Log successful initialization
    envConsole.log('Supabase client initialized successfully');
  } else {
    // Create a dummy client that will throw meaningful errors
    supabase = {
      from: () => { throw new Error('Supabase not properly configured'); },
      auth: { getUser: () => Promise.reject(new Error('Supabase not properly configured')) },
      rpc: () => Promise.reject(new Error('Supabase not properly configured'))
    } as any;
    
    console.error('Supabase client not initialized due to invalid configuration');
  }
} catch (error) {
  console.error('Failed to create Supabase client:', error);
  // Create a dummy client
  supabase = {
    from: () => { throw new Error('Supabase client creation failed'); },
    auth: { getUser: () => Promise.reject(new Error('Supabase client creation failed')) },
    rpc: () => Promise.reject(new Error('Supabase client creation failed'))
  } as any;
}

export { supabase };
export default supabase;

// Only log critical configuration issues in development mode
if (isDevelopment() && (!config.supabase.url || !config.supabase.anonKey)) {
  console.warn('Supabase configuration issue: Missing URL or key');
}

// Check if we already have a client instance in the global space
const globalWithSupabase = globalThis as unknown as { 
  supabaseClient?: ReturnType<typeof createClient<any>> 
};

// Create a function to handle common Supabase errors without spamming the console
export function handleSupabaseError(error: any, context: string = '') {
  // Only log errors in development mode or if they're critical
  if (isDevelopment()) {
    // Skip logging for common non-critical errors
    const nonCriticalErrors = [
      'Failed to fetch', 
      'Network Error',
      'JWT expired',
      'No rows found',
      'User not found',
      'Invalid login credentials'
    ];
    
    const shouldLog = !nonCriticalErrors.some(msg => 
      error && error.message && error.message.includes(msg)
    );
    
    if (shouldLog) {
      console.error(`Supabase error${context ? ' in ' + context : ''}:`, error);
    }
  }

  // Track API errors for monitoring
  if (error?.status === 404) {
    trackApiError(error, context);
  }
  
  return error;
}

// Track API errors for monitoring purposes
const API_ERRORS = {
  errors: [] as Array<{endpoint: string; error: any; timestamp: number; context: string}>,
  getErrors() { return this.errors; },
  clearErrors() { this.errors = []; }
};

export function trackApiError(error: any, context: string = '') {
  let endpoint = '';
  
  // Try to extract the endpoint from the error
  if (error?.url) {
    const url = new URL(error.url);
    endpoint = url.pathname;
  } else if (context) {
    endpoint = context;
  }
  
  API_ERRORS.errors.push({
    endpoint,
    error,
    timestamp: Date.now(),
    context
  });
  
  // Keep only the last 100 errors
  if (API_ERRORS.errors.length > 100) {
    API_ERRORS.errors.shift();
  }
}

export function getApiErrors() {
  return API_ERRORS.getErrors();
}

export function clearApiErrors() {
  API_ERRORS.clearErrors();
}

// Add error handling to commonly used methods
const originalFrom = supabase.from.bind(supabase);
supabase.from = function(table: string) {
  const builder = originalFrom(table);
  const originalSelect = builder.select.bind(builder);
  
  builder.select = function(...args: any[]) {
    const query = originalSelect(...args);
    
    // Intercept the original fetch call
    const originalFetch = query.then.bind(query);
    query.then = function(onFulfilled: any, onRejected: any) {
      return originalFetch(
        onFulfilled,
        (error: any) => {
          if (error?.status === 404) {
            trackApiError(error, `supabase.from('${table}').select`);
          }
          return onRejected ? onRejected(error) : Promise.reject(error);
        }
      );
    };
    
    return query;
  };
  
  return builder;
};

// Intercept RPC calls
const originalRpc = supabase.rpc.bind(supabase);
supabase.rpc = function(fn: string, ...args: any[]) {
  const query = originalRpc(fn, ...args);
  
  // Intercept the original fetch call
  const originalFetch = query.then.bind(query);
  query.then = function(onFulfilled: any, onRejected: any) {
    return originalFetch(
      onFulfilled,
      (error: any) => {
        if (error?.status === 404) {
          trackApiError(error, `supabase.rpc('${fn}')`);
        }
        return onRejected ? onRejected(error) : Promise.reject(error);
      }
    );
  };
  
  return query;
};

// Add a simple health check method to test API connectivity
export async function checkApiHealth() {
  try {
    // Check if the _health table exists, if not this will fail gracefully
    const { error } = await supabase.from('_health').select('count', { count: 'exact', head: true });
    
    return { 
      status: error ? 'error' : 'ok',
      error: error
    };
  } catch (error) {
    return {
      status: 'error',
      error: error
    };
  }
}

/**
 * Health check for Supabase connection
 * Returns true if connection is working, false otherwise
 * This can be used to test connectivity without generating console errors
 */
export async function checkSupabaseHealth(): Promise<boolean> {
  try {
    const { error } = await supabase.from('_health').select('count', { count: 'exact', head: true });
    
    // If we get a 404, the table doesn't exist but the connection is working
    if (error && error.code === '42P01') {
      return true;
    }
    
    return !error;
  } catch (error) {
    return false;
  }
} 